import { useMemo, useState } from "react";
import type { PromotionDiffResponse } from "@starsach/shared-types";
import { useClearOverride, usePromote, usePromotionPreview } from "../../api/hooks";
import { useEnvironmentName } from "../../components/Environment";
import { Card, ErrorState, LoadingState, RelativeTime, btnPrimary } from "../../components/ui";

type Choice = "take_source" | "keep_target";

/**
 * ADR-0007's promotion, as a screen. Two rules drive the whole layout:
 *
 *  - When nothing is contested this is one button and no questions. A
 *    confirmation that fires every time is one that gets clicked through
 *    blind on the day it matters.
 *  - The default on every contested field is to keep the target's value.
 *    Overwriting a deliberate production change is the destructive
 *    direction, so it is opt-in.
 */
export function PromotionPanel({
  environmentId,
  profileId,
  chain,
}: {
  environmentId: string;
  profileId: string;
  chain: string[];
}) {
  const nameOf = useEnvironmentName();
  const below = chain.slice(0, Math.max(0, chain.indexOf(environmentId)));
  const [source, setSource] = useState(below.at(-1));
  const preview = usePromotionPreview(environmentId, profileId, source);
  const promote = usePromote(environmentId);
  const [choices, setChoices] = useState<Record<string, Choice>>({});

  const diff = preview.data;
  const decisions = useMemo(
    () => (diff?.contested ?? []).map((c) => ({ field: c.field, choice: choices[c.field] ?? ("keep_target" as Choice) })),
    [diff, choices],
  );

  // ADR-0008: a customer with nothing below this environment has no
  // promotion at all, and must not be shown the machinery. Checked after
  // the hooks so their order never varies between renders.
  if (below.length === 0) return null;

  return (
    <Card title="Promote">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label htmlFor="promotion-source" className="text-neutral-600 dark:text-neutral-400">Promote from</label>
        <select
          id="promotion-source"
          className="rounded border border-neutral-300 bg-white px-2 py-1 dark:border-neutral-700 dark:bg-neutral-900"
          value={source ?? ""}
          onChange={(e) => { setSource(e.target.value); setChoices({}); }}
        >
          {below.map((id) => <option key={id} value={id}>{nameOf(id)}</option>)}
        </select>
        <span className="text-neutral-500">into {nameOf(environmentId)}</span>
      </div>

      {preview.isPending && source && <LoadingState label="Comparing" />}
      {preview.isError && <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />}

      {diff && (
        <>
          <p className="mt-3 text-sm text-neutral-600 dark:text-neutral-400">
            v{diff.sourceVersion} into v{diff.targetVersion}: {diff.uncontested.length} to apply, {diff.contested.length} to decide, {diff.unchanged} unchanged.
            {diff.targetInventory && ` Computed against ${nameOf(environmentId)}'s inventory v${diff.targetInventory.version}${diff.targetInventory.state === "changing" ? ", which is changing" : ""}.`}
          </p>
          {diff.targetInventory?.state === "changing" && (
            <p role="status" className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
              {nameOf(environmentId)} is mid-change - {diff.targetInventory.burst?.calls ?? "several"} configuration updates in progress. Applying will be refused until it settles; the preview is still worth reading.
            </p>
          )}

          {diff.skipsEnvironments.length > 0 && (
            <p role="status" className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
              This skips {diff.skipsEnvironments.join(", ")}, which will be left behind what is above it.
            </p>
          )}

          {diff.masked.length > 0 && <MaskedList masked={diff.masked} environmentId={environmentId} profileId={profileId} />}

          {diff.contested.length > 0 && (
            <ContestedList contested={diff.contested} choices={choices} onChange={setChoices} />
          )}

          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              className={btnPrimary}
              disabled={promote.isPending || (diff.uncontested.length === 0 && diff.contested.length === 0)}
              // ADR-0018 section 4: the apply names the inventory version the preview saw; central refuses if it moved.
              onClick={() => source && promote.mutate({ profileId, sourceEnvironmentId: source, decisions, ...(diff.targetInventory ? { targetInventoryVersion: diff.targetInventory.version } : {}) })}
            >
              {diff.requiresApproval ? "Request promotion" : "Promote"}
            </button>
            {diff.uncontested.length > 0 && diff.contested.length === 0 && (
              <span className="text-sm text-neutral-500">Nothing is contested.</span>
            )}
          </div>

          {promote.isSuccess && (
            <p role="status" className="mt-3 text-sm text-green-800 dark:text-green-300">
              {promote.data.status === "applied"
                ? `Promoted as v${promote.data.configVersion}: ${promote.data.applied.length} applied, ${promote.data.kept.length} kept.`
                : `Sent for approval (${promote.data.contested} contested).`}
            </p>
          )}
          {promote.isError && <ErrorState error={promote.error} />}
        </>
      )}
    </Card>
  );
}

/**
 * The live risk in the other direction: an active override masks a
 * promoted change, leaving it deployed and inaudible until the override
 * expires, with the promotion reporting success.
 */
function MaskedList({ masked, environmentId, profileId }: { masked: PromotionDiffResponse["masked"]; environmentId: string; profileId: string }) {
  const clear = useClearOverride(environmentId);
  return (
    <div role="alert" className="mt-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950">
      <p className="font-medium text-amber-900 dark:text-amber-100">
        {masked.length} incoming {masked.length === 1 ? "change is" : "changes are"} hidden by an active override
      </p>
      <ul className="mt-2 space-y-2">
        {masked.map((m) => (
          <li key={m.field}>
            <code className="text-xs">{m.field}</code>
            <div className="text-amber-900 dark:text-amber-100">
              Playing <q>{String(m.override.value)}</q> instead, set by {m.override.setBy} <RelativeTime iso={m.override.setAt} />
              {m.override.expiresAt ? <> until <RelativeTime iso={m.override.expiresAt} /></> : " with no expiry"}.
            </div>
            {/* Keep it, and the change stages behind it; or end it now. */}
            <button
              type="button"
              className="mt-1 text-xs underline"
              disabled={clear.isPending}
              onClick={() => clear.mutate({ profileId, field: m.field })}
            >
              End this override now
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ContestedList({
  contested,
  choices,
  onChange,
}: {
  contested: PromotionDiffResponse["contested"];
  choices: Record<string, Choice>;
  onChange: (next: Record<string, Choice>) => void;
}) {
  const setAll = (choice: Choice) => onChange(Object.fromEntries(contested.map((c) => [c.field, choice])));

  return (
    <fieldset className="mt-4">
      <legend className="text-sm font-medium">
        {contested.length} field{contested.length === 1 ? "" : "s"} changed here since
      </legend>
      {/* Select-all and keep-all, so a long list resolves in one action. */}
      <div className="mt-2 flex gap-2">
        <button type="button" className="rounded border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700" onClick={() => setAll("take_source")}>
          Take all incoming
        </button>
        <button type="button" className="rounded border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700" onClick={() => setAll("keep_target")}>
          Keep all current
        </button>
      </div>
      <ul className="mt-3 space-y-3">
        {contested.map((c) => {
          const choice = choices[c.field] ?? "keep_target";
          return (
            <li key={c.field} className="rounded border border-neutral-200 p-3 dark:border-neutral-800">
              <code className="text-xs">{c.field}</code>
              <p className="mt-1 text-xs text-neutral-500">
                {c.recommendation === "keep_target"
                  ? <>Changed here <RelativeTime iso={c.targetChangedAt} />, after the incoming change. Probably still wanted.</>
                  : <>Changed here <RelativeTime iso={c.targetChangedAt} />, before the incoming change. Probably superseded.</>}
              </p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <label className={`flex cursor-pointer gap-2 rounded border p-2 text-sm ${choice === "keep_target" ? "border-accent" : "border-neutral-200 dark:border-neutral-800"}`}>
                  <input type="radio" name={`choice-${c.field}`} checked={choice === "keep_target"} onChange={() => onChange({ ...choices, [c.field]: "keep_target" })} />
                  <span><span className="block text-xs text-neutral-500">Keep current</span>{String(c.targetValue)}</span>
                </label>
                <label className={`flex cursor-pointer gap-2 rounded border p-2 text-sm ${choice === "take_source" ? "border-accent" : "border-neutral-200 dark:border-neutral-800"}`}>
                  <input type="radio" name={`choice-${c.field}`} checked={choice === "take_source"} onChange={() => onChange({ ...choices, [c.field]: "take_source" })} />
                  <span><span className="block text-xs text-neutral-500">Take incoming</span>{String(c.sourceValue)}</span>
                </label>
              </div>
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}
